const MARKER = 'sendSoftphoneMetrics success'

/** Per-second loss above this is the cumulative first sample, not a one-second delta. */
const MAX_PACKETS_LOST = 250
/** Per-second packet count above this is a cumulative opener, not a one-second delta. */
const MAX_PACKETS_COUNT = 1000
const MAX_TIMING_MS = 10_000
const MAX_BATCH = 120

export type WebRtcDirection = 'input' | 'output'

export type WebRtcSample = {
  direction: WebRtcDirection
  /** Wall-clock time of the sample, unix ms. */
  atMs: number
  /** CCP stats timestamp, used to drop a duplicate delivery of the same batch. */
  rawTimestamp: number
  packetsLost?: number
  /** Per-second RTP packets. 0 means the stream produced nothing that second. */
  packetsCount?: number
  /**
   * Raw CCP audioLevel. Not rescaled. WebRTC's audioLevel is linear 0–1;
   * this Streams build compares it to LOW_AUDIO_LEVEL_THRESHOLD = 1.
   * 0 is real silence and is kept.
   */
  audioLevel?: number
  jitterMs?: number
  rttMs?: number
}

type RawStat = {
  softphoneStreamType?: unknown
  timestamp?: unknown
  packetsLost?: unknown
  packetsCount?: unknown
  audioLevel?: unknown
  jitterBufferMillis?: unknown
  roundTripTimeMillis?: unknown
}

/**
 * Pull one-second RTP samples out of a CCP log line:
 * `sendSoftphoneMetrics success[{softphoneStreamType, packetsLost, ...}]`.
 * Stats timestamps are page-clock milliseconds or wall-clock Dates. The newest
 * sample is anchored to `receivedAtMs` and the earlier ones keep their spacing.
 * Returns null when the line is not a softphone-metrics log.
 */
export function parseSoftphoneMetricsLog(
  text: string,
  receivedAtMs: number,
): WebRtcSample[] | null {
  if (!text || !Number.isFinite(receivedAtMs)) return null
  const markerAt = text.indexOf(MARKER)
  if (markerAt < 0) return null
  const jsonAt = text.indexOf('[', markerAt + MARKER.length)
  if (jsonAt < 0) return null

  const rows = parseJsonArray(text.slice(jsonAt))
  if (!rows || rows.length === 0) return null

  const stamped: {row: RawStat; direction: WebRtcDirection; timestamp: number}[] = []
  for (const row of rows) {
    const direction = normalizeDirection(row.softphoneStreamType)
    const timestamp = finiteNumber(row.timestamp)
    if (!direction || timestamp == null) continue
    stamped.push({row, direction, timestamp})
  }
  if (stamped.length === 0) return null

  let newest = stamped[0].timestamp
  for (const item of stamped) {
    if (item.timestamp > newest) newest = item.timestamp
  }
  const shift = receivedAtMs - newest

  const samples: WebRtcSample[] = []
  for (const item of stamped) {
    const atMs = Math.round(item.timestamp + shift)
    if (!Number.isFinite(atMs) || atMs < 1_000_000_000_000) continue

    const sample: WebRtcSample = {
      direction: item.direction,
      atMs,
      rawTimestamp: item.timestamp,
    }
    const lost = nonNegativeInt(item.row.packetsLost)
    if (lost != null && lost <= MAX_PACKETS_LOST) sample.packetsLost = lost
    const count = nonNegativeInt(item.row.packetsCount)
    if (count != null && count <= MAX_PACKETS_COUNT) sample.packetsCount = count
    const level = nonNegativeNumber(item.row.audioLevel)
    if (level != null) sample.audioLevel = level
    const jitter = nonNegativeInt(item.row.jitterBufferMillis)
    if (jitter != null && jitter <= MAX_TIMING_MS) sample.jitterMs = jitter
    const rtt = nonNegativeInt(item.row.roundTripTimeMillis)
    if (rtt != null && rtt <= MAX_TIMING_MS) sample.rttMs = rtt
    if (
      sample.packetsLost == null &&
      sample.packetsCount == null &&
      sample.audioLevel == null &&
      sample.jitterMs == null &&
      sample.rttMs == null
    ) {
      continue
    }
    samples.push(sample)
  }

  if (samples.length === 0) return null
  if (samples.length <= MAX_BATCH) return samples
  return samples.slice(samples.length - MAX_BATCH)
}

function parseJsonArray(text: string): RawStat[] | null {
  let depth = 0
  let inString = false
  let escape = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inString) {
      if (escape) escape = false
      else if (ch === '\\') escape = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') {
      inString = true
      continue
    }
    if (ch === '[') depth++
    else if (ch === ']') {
      depth--
      if (depth === 0) {
        try {
          const parsed = JSON.parse(text.slice(0, i + 1)) as unknown
          return Array.isArray(parsed) ? (parsed as RawStat[]) : null
        } catch {
          return null
        }
      }
    }
  }
  return null
}

function normalizeDirection(value: unknown): WebRtcDirection | null {
  const text = String(value ?? '').trim().toLowerCase()
  if (text === 'audio_input' || text === 'input') return 'input'
  if (text === 'audio_output' || text === 'output') return 'output'
  return null
}

/**
 * CCP's media stats timestamp is a Date (lily-rtc passes `new Date` into the
 * stats object). JSON.stringify turns that into an ISO string, and a Date
 * itself is not a finite number. Both still mean wall-clock milliseconds.
 */
function finiteNumber(value: unknown): number | null {
  if (value instanceof Date) {
    const n = value.getTime()
    return Number.isFinite(n) ? n : null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const asNumber = Number(trimmed)
    if (Number.isFinite(asNumber)) return asNumber
    const parsed = Date.parse(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function nonNegativeInt(value: unknown): number | null {
  const n = finiteNumber(value)
  if (n == null || n < 0) return null
  return Math.round(n)
}

/** Keep the raw magnitude. 0 is silence, not a missing sample. */
function nonNegativeNumber(value: unknown): number | null {
  const n = finiteNumber(value)
  if (n == null || n < 0) return null
  return n
}
